import { apiClient } from '@/core/api/client';
import type {
  ResponsibilityAssignment,
  ResponsibilityFilters,
  CreateResponsibilityDto,
  UpdateResponsibilityDto,
  ApiResponse,
  User
} from '../types';

export const taskResponsibilitiesAPI = {
  // Get all responsibilities with filters
  async getResponsibilities(filters?: ResponsibilityFilters): Promise<ApiResponse<ResponsibilityAssignment[]>> {
    try {
      const params = new URLSearchParams();
      if (filters?.taskType && filters.taskType !== 'all') {
        params.append('taskType', filters.taskType);
      }
      if (filters?.isActive !== undefined) {
        params.append('isActive', filters.isActive.toString());
      }
      if (filters?.userId) {
        params.append('userId', filters.userId);
      }

      return apiClient.get<ApiResponse<ResponsibilityAssignment[]>>(`/api/admin/task-responsibilities?${params}`);
    } catch (error) {
      throw new Error('Failed to fetch task responsibilities');
    }
  },

  // Get users eligible for responsibilities (admin, bureau, expert roles)
  async getEligibleUsers(roles?: string[]): Promise<ApiResponse<User[]>> {
    try {
      const params = new URLSearchParams();
      if (roles && roles.length > 0) {
        params.append('roles', roles.join(','));
      }

      return apiClient.get<ApiResponse<User[]>>(`/api/admin/task-responsibilities/users?${params}`);
    } catch (error) {
      throw new Error('Failed to fetch eligible users');
    }
  },

  // Create new responsibility assignment
  async createResponsibility(data: CreateResponsibilityDto): Promise<ApiResponse<ResponsibilityAssignment>> {
    try {
      return apiClient.post<ApiResponse<ResponsibilityAssignment>>('/api/admin/task-responsibilities', data);
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data;
      }
      throw new Error('Failed to create responsibility');
    }
  },

  // Update responsibility
  async updateResponsibility(
    responsibilityId: string,
    data: UpdateResponsibilityDto
  ): Promise<ApiResponse<ResponsibilityAssignment>> {
    try {
      return apiClient.put<ApiResponse<ResponsibilityAssignment>>(`/api/admin/task-responsibilities/${responsibilityId}`, data);
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data;
      }
      throw new Error('Failed to update responsibility');
    }
  },

  // Delete responsibility
  async deleteResponsibility(responsibilityId: string): Promise<ApiResponse<void>> {
    try {
      return apiClient.delete<ApiResponse<void>>(`/api/admin/task-responsibilities/${responsibilityId}`);
    } catch (error) {
      throw new Error('Failed to delete responsibility');
    }
  },

  // Bulk update responsibilities for a task type
  async bulkUpdateResponsibilities(
    taskType: string,
    responsibilities: Array<{
      userId: string;
      daysPerWeek: number;
      isActive: boolean;
    }>
  ): Promise<ApiResponse<ResponsibilityAssignment[]>> {
    try {
      return apiClient.post<ApiResponse<ResponsibilityAssignment[]>>(`/api/admin/task-responsibilities/bulk`, {
        taskType,
        responsibilities
      });
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data;
      }
      throw new Error('Failed to bulk update responsibilities');
    }
  }
};
